import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'

import { cn } from './cn'
import { t } from '../copy'
import { Button } from './button'

/**
 * 弹窗。改服务商账号、看凭证、确认一件碰得到钱的事，都用它。
 *
 * Radix 负责焦点陷阱、Esc 关闭、滚动锁、aria——这些自己写一定会漏。
 * 外观全是自己按 token 写的。
 *
 * 有意没做的一件事：**这里不提供「确认/取消」的默认按钮**。
 * 危险动作的按钮文案必须是具体的（「就这一次」「24 小时内都可以」「先别动」），
 * 给一对通用的确定取消，只会让人写出「该操作不可逆，请确认」那种弹窗。
 */
export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  /** 标题下面那行说明。说清这个弹窗是干嘛的。 */
  description?: ReactNode
  children?: ReactNode
  /** 底部按钮区。自己传具体的按钮，不要用通用的确定取消。 */
  footer?: ReactNode
  className?: string
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-[rgba(0,0,0,0.5)]" /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */ />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[min(560px,calc(100vw-48px))] max-h-[85vh] overflow-auto',
            'rounded-[12px] border border-m-line2 bg-m-bg1 p-[18px] shadow-[var(--m-pop)]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            className,
          )}
        >
          <Dialog.Title className="m-0 text-[16px] font-semibold text-m-t1">{title}</Dialog.Title> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {description ? (
            <Dialog.Description className="mb-0 mt-[6px] text-[13px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {description}
            </Dialog.Description>
          ) : null}

          <div className="mt-npc-card">{children}</div>

          {footer ? <div className="mt-npc-card flex gap-npc-gap-sm justify-end">{footer}</div> : null}

          <Dialog.Close asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('ui.close')}
              className="absolute right-npc-gap-lg top-npc-gap-lg"
            >
              ×
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
